import React, { useState, useEffect } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Topbar from "../Components/Topbar";
import config from "../Config";
import ImageSelectDialog from "../Components/ImageSelectDialog";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const AutoHome = () => {
  const [services, setServices] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [categories, setCategories] = useState([]);
  const [combos, setCombos] = useState([]);
  const [loading, setLoading] = useState(true);

  const [sections, setSections] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showImageSelectDialog, setShowImageSelectDialog] = useState(false);
  const [imagePreview, setImagePreview] = useState("");
  const [imageFile, setImageFile] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const [formData, setFormData] = useState({
    view: "",
    type: "",
    title: "",
    subtitle: "",
    image: "",
    color: "",
    label: "",
    items: [],
  });

  // Helper function to get unique identifier for an item
  const getItemId = (item) => {
    return item.id || item.service_id || item.sub_category_id || item.category_id || item.combo_id;
  };

  useEffect(() => {
    fetchServices();
    fetchSubCategories();
    fetchCategories();
    fetchCombos();
    fetchSections();
  }, []);

  const fetchServices = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${config.apiUrl}/api/v1/admin/service?disable_pagination=true`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setServices(data);
      } else {
        throw new Error("Failed to fetch services");
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const fetchSubCategories = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${config.apiUrl}/api/v1/admin/subcategories?disable_pagination=true`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setSubCategories(data);
      } else {
        throw new Error("Failed to fetch subcategories");
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${config.apiUrl}/api/v1/admin/categories?disable_pagination=true`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setCategories(data);
        setLoading(false);
      } else {
        throw new Error("Failed to fetch categories");
      }
    } catch (error) {
      toast.error(error.message);
      setLoading(false);
    }
  };

  const fetchCombos = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${config.apiUrl}/api/v1/admin/combo?disable_pagination=true`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        const result = await response.json();
        setCombos(Array.isArray(result.data) ? result.data : []);
      } else {
        throw new Error("Failed to fetch combos");
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const fetchSections = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${config.apiUrl}/api/v1/admin/auto-home?disable_pagination=true`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.ok) {
        const data = await response.json();
        if (Array.isArray(data)) {
          setSections(data || []);
        }
      }
      setLoading(false);
    } catch (error) {
      console.error("Error fetching sections:", error);
      toast.error("Failed to fetch sections");
      setLoading(false);
    }
  };

  const updateSectionOrder = async (sectionIds) => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${config.apiUrl}/api/v1/admin/auto-home/order`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ section_ids: sectionIds }),
      });

      if (!response.ok) {
        throw new Error("Failed to update section order");
      } else {
        toast.success("Section order updated successfully");
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleImageSelect = (file) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      setImageFile(file);
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const resetForm = () => {
    setFormData({
      view: "",
      type: "",
      title: "",
      subtitle: "",
      image: "",
      color: "",
      label: "",
      items: [],
    });
    setImageFile(null);
    setImagePreview("");
    setIsEditing(false);
    setEditingId(null);
  };

  const handleEdit = (item) => {
    const id = item.section_id;
    const section = item.section;
    setFormData({
      view: section.view,
      type: section.type,
      title: section.title,
      subtitle: section.subtitle || "",
      image: section.image || "",
      color: section.color || "",
      label: section.label || "",
      // Use full items directly when editing
      items: section.items || [],
    });
    setImagePreview(section.image || "");
    setIsEditing(true);
    setEditingId(id);
    setShowModal(true);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = sections.findIndex((s) => s.section_id === active.id);
      const newIndex = sections.findIndex((s) => s.section_id === over.id);

      const newSections = arrayMove(sections, oldIndex, newIndex);
      setSections(newSections);

      // Update order in backend
      const sectionIds = newSections.map((section) => section.section_id);
      await updateSectionOrder(sectionIds);
    }
  };

  const SortableSection = ({ section }) => {
    const { attributes, listeners, setNodeRef, transform, transition } =
      useSortable({
        id: section.section_id,
      });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
    };

    return (
      <tr ref={setNodeRef} style={style} className="hover:bg-gray-50">
        <td className="px-6 py-3 cursor-move" {...attributes} {...listeners}>
          <div className="flex items-center">
            <svg
              className="w-4 h-4 mr-2 text-gray-400"
              fill="currentColor"
              viewBox="0 0 24 24"
            >
              <path d="M8 6h8v2H8zm0 5h8v2H8zm0 5h8v2H8z" />
            </svg>
            {section.section.title}
          </div>
        </td>
        <td className="px-6 py-3">{section.section.view}</td>
        <td className="px-6 py-3">{section.section.type}</td>
        <td className="px-6 py-3 text-left">
          {section.section.items.map((item) => (
            <div
              key={getItemId(item) || item.id}
            >
              {item.text || item.title || item.name}
              <br />
            </div>
          ))}
        </td>
        <td className="px-6 py-3">
          <button
            onClick={() => handleEdit(section)}
            className="text-indigo-600 hover:text-indigo-900 mr-4"
          >
            Edit
          </button>
          {section.deleted_at ? (
            <button
              onClick={() => handleRestore(section.section_id)}
              className="text-green-600 hover:text-green-900"
            >
              Restore
            </button>
          ) : (
            <button
              onClick={() => handleDelete(section.section_id)}
              className="text-red-600 hover:text-red-900"
            >
              Delete
            </button>
          )}
        </td>
      </tr>
    );
  };

  const handleDelete = async (sectionId) => {
    if (!window.confirm("Are you sure you want to delete this section?")) {
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${config.apiUrl}/api/v1/admin/auto-home/${sectionId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        toast.success("Section deleted successfully");
        fetchSections();
      } else {
        throw new Error("Failed to delete section");
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleRestore = async (sectionId) => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${config.apiUrl}/api/v1/admin/auto-home/${sectionId}/restore`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        toast.success("Section restored successfully");
        fetchSections();
      } else {
        throw new Error("Failed to restore section");
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate required fields
    if (!formData.view) {
      toast.error("Please select a view type");
      return;
    }
    if (formData.view !== "slider" && formData.view !== "text_slider" && formData.view !== "combo" && !formData.type) {
      toast.error("Please select a content type");
      return;
    }
    if (formData.view !== "slider" && formData.view !== "text_slider" && formData.view !== "combo" && !formData.title) {
      toast.error("Please enter a title");
      return;
    }
    if (!formData.items || formData.items.length === 0) {
      let errorMessage = "Please select at least one item";
      if (formData.view === "text_slider") {
        errorMessage = "Please add at least one text item";
      } else if (formData.view === "combo") {
        errorMessage = "Please select a combo";
      }
      toast.error(errorMessage);
      return;
    }

    const token = localStorage.getItem("token");
    const sectionId = isEditing ? editingId : crypto.randomUUID();

    try {
      let imageUrl = formData.image;

      if (imageFile) {
        const formData = new FormData();
        formData.append("file", imageFile);

        const uploadResponse = await fetch(
          "https://hspbreguarptwwdfhtuj.supabase.co/functions/v1/image-upload",
          {
            method: "POST",
            headers: {
              "X-Client": "ADMIN",
              Authorization: `Bearer ${token}`,
            },
            body: formData,
          }
        );

        if (uploadResponse.ok) {
          const uploadData = await uploadResponse.json();
          imageUrl = uploadData[0];
          toast.success("Image uploaded successfully!");
        } else {
          toast.error("Failed to upload image");
          throw new Error("Failed to upload image");
        }
      }

      const url = isEditing
        ? `${config.apiUrl}/api/v1/admin/auto-home/${editingId}`
        : `${config.apiUrl}/api/v1/admin/auto-home`;

      const method = isEditing ? "PUT" : "POST";

      formData.image = imageUrl;

      // Make all empty entries in the formData null
      Object.keys(formData).forEach((key) => {
        if (typeof formData[key] === "string" && formData[key].trim() === "") {
          formData[key] = null;
        }
      });

      // Send the full objects directly
      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          section_id: sectionId,
          section: {
            ...formData,
            // Handle different view types for items
            items: formData.view === "slider" 
              ? formData.items.map(item => {
                  return {
                    type: item.type,
                    id: getItemId(item),
                    image: item.image || null
                  };
                })
              : formData.view === "text_slider"
              ? formData.items.map(item => {
                  return {
                    title: item.title,
                    text: item.text,
                    id: item.id || crypto.randomUUID()
                  };
                })
              : formData.view === "combo"
              ? formData.items.map(item => {
                  return {
                    type: item.type,
                    combo_id: getItemId(item),
                    title: item.title,
                    subtitle: item.subtitle,
                    description: item.description,
                    image: item.image || null
                  };
                })
              : formData.items,
          },
        }),
      });

      if (response.ok) {
        fetchSections();
        toast.success(
          `Section ${isEditing ? "updated" : "created"} successfully`
        );
        resetForm();
        setShowModal(false);
      } else {
        throw new Error(`Failed to ${isEditing ? "update" : "create"} section`);
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const SortableItem = ({ id, title, onRemove, view, item, onImageUpload }) => {
    const { attributes, listeners, setNodeRef, transform, transition } =
      useSortable({ id });
    const [showImageSelectDialog, setShowImageSelectDialog] = useState(false);

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
    };

    const handleImageSelect = (file) => {
      if (file) {
        onImageUpload(file);
      }
    };

    return (
      <div
        ref={setNodeRef}
        style={style}
        className="flex items-center justify-between p-2 mb-2 bg-white border border-gray-200 rounded-md"
      >
        <div className="flex items-center">
          <div
            className="flex items-center cursor-move"
            {...attributes}
            {...listeners}
          >
            <svg
              className="w-4 h-4 mr-2 text-gray-400"
              fill="currentColor"
              viewBox="0 0 24 24"
            >
              <path d="M8 6h8v2H8zm0 5h8v2H8zm0 5h8v2H8z" />
            </svg>
            {title}
          </div>
          {view === "slider" && (
            <div className="ml-4">
              {item.image ? (
                <img
                  src={item.image}
                  alt="Preview"
                  className="w-16 h-20 object-cover rounded-md"
                />
              ) : (
                <button
                  type="button"
                  onClick={() => setShowImageSelectDialog(true)}
                  className="px-2 py-1 text-sm bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  Upload Image
                </button>
              )}
            </div>
          )}
        </div>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          className="p-1 hover:bg-gray-100 rounded-full"
          type="button"
        >
          ×
        </button>

        {showImageSelectDialog && (
          <ImageSelectDialog
            open={showImageSelectDialog}
            onClose={() => setShowImageSelectDialog(false)}
            onFileProcessed={(file) => {
              handleImageSelect(file);
              setShowImageSelectDialog(false);
            }}
            aspectRatio={4/3}
          />
        )}
      </div>
    );
  };

  const ItemSelector = ({ type, selectedItems, onItemsChange, view, categories, subCategories, services, combos }) => {
    const [searchTerm, setSearchTerm] = useState("");
    const [availableItems, setAvailableItems] = useState([]);
    const [selectedType, setSelectedType] = useState("");

    const sensors = useSensors(
      useSensor(PointerSensor),
      useSensor(KeyboardSensor, {
        coordinateGetter: sortableKeyboardCoordinates,
      })
    );

    useEffect(() => {
      // For combo view, show all combos
      if (view === "combo") {
        setAvailableItems(combos.map(item => ({ ...item, type: 'combo' })));
        return;
      }

      // For slider view, we'll show all items when no type is selected
      if (view === "slider" && !selectedType) {
        // Combine all items and add type information
        const allItems = [
          ...categories.map(item => ({ ...item, type: 'category' })),
          ...subCategories.map(item => ({ ...item, type: 'subcategory' })),
          ...services.map(item => ({ ...item, type: 'service' }))
        ];
        setAvailableItems(allItems);
        return;
      }

      // Use the appropriate type: selectedType for slider view, type prop for other views
      const currentType = view === "slider" ? selectedType : type;

      switch (currentType) {
        case "category":
          setAvailableItems(categories.map(item => ({ ...item, type: 'category' })));
          break;
        case "subcategory":
          setAvailableItems(subCategories.map(item => ({ ...item, type: 'subcategory' })));
          break;
        case "service":
          setAvailableItems(services.map(item => ({ ...item, type: 'service' })));
          break;
        default:
          setAvailableItems([]);
      }
    }, [selectedType, type, view, categories, subCategories, services, combos]);

    const handleDragEnd = (event) => {
      const { active, over } = event;

      if (active.id !== over.id) {
        const oldIndex = selectedItems
          .map((e) => getItemId(e))
          .indexOf(active.id);
        const newIndex = selectedItems
          .map((e) => getItemId(e))
          .indexOf(over.id);
        onItemsChange(arrayMove(selectedItems, oldIndex, newIndex));
      }
    };

    const filteredItems = availableItems.filter((item) =>
      (item.title || item.name).toLowerCase().includes(searchTerm.toLowerCase())
    );

    const isItemSelected = (item) => {
      return selectedItems.some(
        (selected) => getItemId(selected) === getItemId(item)
      );
    };

    const getItemType = (item) => {
      // Use the explicit type property we set when mapping items
      return item.type || "unknown";
    };

    const handleImageUpload = async (item, file) => {
      try {
        const formData = new FormData();
        formData.append("file", file);

        const token = localStorage.getItem("token");
        const uploadResponse = await fetch(
          "https://hspbreguarptwwdfhtuj.supabase.co/functions/v1/image-upload",
          {
            method: "POST",
            headers: {
              "X-Client": "ADMIN",
              Authorization: `Bearer ${token}`,
            },
            body: formData,
          }
        );

        if (uploadResponse.ok) {
          const uploadData = await uploadResponse.json();
          const updatedItems = selectedItems.map(selectedItem => {
            if (getItemId(selectedItem) === getItemId(item)) {
              return { ...selectedItem, image: uploadData[0] };
            }
            return selectedItem;
          });
          onItemsChange(updatedItems);
          toast.success("Image uploaded successfully!");
        } else {
          throw new Error("Failed to upload image");
        }
      } catch (error) {
        toast.error(error.message);
      }
    };

    return (
      <div className="space-y-4">
        {view === "slider" && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Filter by Type (Optional)
            </label>
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            >
              <option value="">All Types</option>
              <option value="category">Category</option>
              <option value="subcategory">Subcategory</option>
              <option value="service">Service</option>
            </select>
          </div>
        )}

        <div className="flex space-x-2">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search items..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="border border-gray-200 rounded-md p-4">
            <h3 className="font-medium mb-2">Available {view === "combo" ? "Combos" : "Items"}</h3>
            {view === "combo" && (
              <p className="text-xs text-gray-500 mb-2">Select only one combo from the list</p>
            )}
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {filteredItems.map((item) => {
                const isSelected = isItemSelected(item);
                const itemType = getItemType(item);
                return (
                  <div
                    key={getItemId(item)}
                    className={`p-2 rounded-md cursor-pointer ${
                      isSelected ? "bg-gray-100" : "hover:bg-gray-50"
                    }`}
                    onClick={() => {
                      if (view === "combo") {
                        // For combo view, allow only single selection
                        if (!isSelected) {
                          onItemsChange([{
                            ...item,
                            type: itemType,
                            image: null
                          }]);
                        } else {
                          onItemsChange([]);
                        }
                      } else {
                        // For other views, allow multiple selections
                        if (!isSelected) {
                          onItemsChange([
                            ...selectedItems,
                            {
                              ...item,
                              type: itemType,
                              image: null
                            }
                          ]);
                        } else {
                          onItemsChange(
                            selectedItems.filter(
                              (selected) => getItemId(selected) !== getItemId(item)
                            )
                          );
                        }
                      }
                    }}
                  >
                    <div className="flex items-center justify-between">
                      <span>{item.title || item.name}</span>
                      <span className="text-xs text-gray-500">{itemType}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="border border-gray-200 rounded-md p-4">
            <h3 className="font-medium mb-2">Selected {view === "combo" ? "Combo" : "Items"}</h3>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={selectedItems.map((item) => getItemId(item))}
                strategy={verticalListSortingStrategy}
              >
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {selectedItems.map((item) => (
                    <SortableItem
                      key={getItemId(item)}
                      id={getItemId(item)}
                      title={item.title || item.name}
                      onRemove={() => {
                        onItemsChange(
                          selectedItems.filter(
                            (selected) => getItemId(selected) !== getItemId(item)
                          )
                        );
                      }}
                      view={view}
                      item={item}
                      onImageUpload={(file) => handleImageUpload(item, file)}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          </div>
        </div>
      </div>
    );
  };

  const TextItemsManager = ({ textItems, onTextItemsChange }) => {
    const [newTitle, setNewTitle] = useState("");
    const [newText, setNewText] = useState("");

    const sensors = useSensors(
      useSensor(PointerSensor),
      useSensor(KeyboardSensor, {
        coordinateGetter: sortableKeyboardCoordinates,
      })
    );

    const addTextItem = () => {
      if (newTitle.trim() !== "" && newText.trim() !== "") {
        const newItem = {
          id: crypto.randomUUID(),
          title: newTitle.trim(),
          text: newText.trim()
        };
        onTextItemsChange([...textItems, newItem]);
        setNewTitle("");
        setNewText("");
      }
    };

    const removeTextItem = (id) => {
      onTextItemsChange(textItems.filter(item => item.id !== id));
    };

    const updateTextItem = (id, newTitle, newText) => {
      onTextItemsChange(
        textItems.map(item => 
          item.id === id ? { ...item, title: newTitle, text: newText } : item
        )
      );
    };

    const handleDragEnd = (event) => {
      const { active, over } = event;

      if (active.id !== over.id) {
        const oldIndex = textItems.findIndex(item => item.id === active.id);
        const newIndex = textItems.findIndex(item => item.id === over.id);
        onTextItemsChange(arrayMove(textItems, oldIndex, newIndex));
      }
    };

    const SortableTextItem = ({ item }) => {
      const { attributes, listeners, setNodeRef, transform, transition } =
        useSortable({ id: item.id });
      const [isEditing, setIsEditing] = useState(false);
      const [editTitle, setEditTitle] = useState(item.title || "");
      const [editText, setEditText] = useState(item.text || "");

      const style = {
        transform: CSS.Transform.toString(transform),
        transition,
      };

      const handleSave = () => {
        if (editTitle.trim() !== "" && editText.trim() !== "") {
          updateTextItem(item.id, editTitle.trim(), editText.trim());
          setIsEditing(false);
        }
      };

      const handleCancel = () => {
        setEditTitle(item.title || "");
        setEditText(item.text || "");
        setIsEditing(false);
      };

      return (
        <div
          ref={setNodeRef}
          style={style}
          className="flex items-center justify-between p-3 mb-2 bg-white border border-gray-200 rounded-md"
        >
          <div className="flex items-center flex-1">
            <div
              className="flex items-center cursor-move mr-3"
              {...attributes}
              {...listeners}
            >
              <svg
                className="w-4 h-4 text-gray-400"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M8 6h8v2H8zm0 5h8v2H8zm0 5h8v2H8z" />
              </svg>
            </div>
            {isEditing ? (
              <div className="flex-1 space-y-2">
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    value={editTitle}
                    onChange={(e) => setEditTitle(e.target.value)}
                    placeholder="Title"
                    className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
                    autoFocus
                  />
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    value={editText}
                    onChange={(e) => setEditText(e.target.value)}
                    placeholder="Text"
                    className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
                  />
                  <button
                    onClick={handleSave}
                    className="px-2 py-1 text-sm bg-green-500 text-white rounded hover:bg-green-600"
                    type="button"
                  >
                    Save
                  </button>
                  <button
                    onClick={handleCancel}
                    className="px-2 py-1 text-sm bg-gray-500 text-white rounded hover:bg-gray-600"
                    type="button"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between flex-1">
                <div className="flex-1">
                  <div className="font-medium text-gray-900">{item.title || "No Title"}</div>
                  <div className="text-sm text-gray-600">{item.text || "No Text"}</div>
                </div>
                <button
                  onClick={() => setIsEditing(true)}
                  className="text-blue-500 hover:text-blue-700 text-sm"
                  type="button"
                >
                  Edit
                </button>
              </div>
            )}
          </div>
          <button
            onClick={() => removeTextItem(item.id)}
            className="p-1 hover:bg-gray-100 rounded-full ml-2"
            type="button"
          >
            ×
          </button>
        </div>
      );
    };

    return (
      <div className="space-y-4">
        <div className="space-y-2">
          <div className="flex space-x-2">
            <input
              type="text"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              placeholder="Enter title..."
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
            />
          </div>
          <div className="flex space-x-2">
            <input
              type="text"
              value={newText}
              onChange={(e) => setNewText(e.target.value)}
              placeholder="Enter text..."
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
              onKeyPress={(e) => e.key === 'Enter' && addTextItem()}
            />
            <button
              type="button"
              onClick={addTextItem}
              className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
              disabled={!newTitle.trim() || !newText.trim()}
            >
              Add
            </button>
          </div>
        </div>

        <div className="border border-gray-200 rounded-md p-4">
          <h3 className="font-medium mb-3">Text Items ({textItems.length})</h3>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={textItems.map(item => item.id)}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {textItems.length === 0 ? (
                  <p className="text-gray-500 text-center py-4">No text items added yet</p>
                ) : (
                  textItems.map((item) => (
                    <SortableTextItem key={item.id} item={item} />
                  ))
                )}
              </div>
            </SortableContext>
          </DndContext>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <Topbar />
      <ToastContainer />
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      ) : (
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="flex justify-between items-center mb-8">
            <h1 className="text-2xl font-bold text-gray-900">Auto Home Sections</h1>
            <button
              onClick={() => setShowModal(true)}
              className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
            >
              Add Section
            </button>
          </div>

          <div className="bg-white rounded-lg shadow overflow-hidden">
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Title
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      View
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Items
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <SortableContext
                    items={sections.map((section) => section.section_id)}
                    strategy={verticalListSortingStrategy}
                  >
                    {sections.map((section) => (
                      <SortableSection
                        key={section.section_id}
                        section={section}
                      />
                    ))}
                  </SortableContext>
                </tbody>
              </table>
            </DndContext>
          </div>

          {showModal && (
            <div className="fixed inset-0 z-[120] flex items-start justify-center overflow-hidden">
              <div
                className="fixed inset-0 bg-gray-500 bg-opacity-75"
                onClick={() => {
                  resetForm();
                  setShowModal(false);
                }}
              ></div>

              <div className="relative z-10 w-full max-w-2xl mx-auto my-4 px-3 sm:px-4">
                <div className="relative bg-white rounded-lg shadow-xl max-h-[calc(100dvh-2rem)] flex flex-col">
                  <div className="sticky top-0 bg-white px-4 sm:px-8 py-4 sm:py-6 border-b border-gray-200 rounded-t-lg">
                    <div className="flex justify-between items-center">
                      <h2 className="text-xl font-bold">
                        {isEditing ? "Edit Section" : "Add New Section"}
                      </h2>
                      <button
                        onClick={() => {
                          resetForm();
                          setShowModal(false);
                        }}
                        className="text-gray-400 hover:text-gray-500"
                      >
                        <span className="text-2xl">×</span>
                      </button>
                    </div>
                  </div>

                  <div className="flex-1 overflow-y-auto px-4 sm:px-8 py-6">
                    <form
                      id="sectionForm"
                      onSubmit={handleSubmit}
                      className="space-y-6"
                    >
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          View <span className="text-red-500">*</span>
                        </label>
                        <select
                          value={formData.view}
                          onChange={(e) =>
                            setFormData({ ...formData, view: e.target.value, type: "" })
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md"
                          required
                        >
                          <option value="">Select view type</option>
                          <option value="grid">Grid</option>
                          <option value="horizontal-list">Horizontal List</option>
                          <option value="vertical-list">Vertical List</option>
                          <option value="page-view">Page View</option>
                          <option value="slider">Slider</option>
                          <option value="text_slider">Text Slider</option>
                          <option value="combo">Combo</option>
                        </select>
                      </div>

                      {formData.view !== "slider" && formData.view !== "text_slider" && formData.view !== "combo" && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Type <span className="text-red-500">*</span>
                          </label>
                          <select
                            value={formData.type}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                type: e.target.value,
                                items: [],
                              })
                            }
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            required
                          >
                            <option value="">Select content type</option>
                            <option value="category">Category</option>
                            <option value="subcategory">Subcategory</option>
                            <option value="service">Service</option>
                          </select>
                        </div>
                      )}

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Title {formData.view !== "slider" && formData.view !== "text_slider" && formData.view !== "combo" && <span className="text-red-500">*</span>}
                        </label>
                        <input
                          type="text"
                          value={formData.title}
                          onChange={(e) =>
                            setFormData({ ...formData, title: e.target.value })
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md"
                          placeholder="e.g. What are you looking for?"
                          required={formData.view !== "slider" && formData.view !== "text_slider" && formData.view !== "combo"}
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Subtitle
                        </label>
                        <input
                          type="text"
                          value={formData.subtitle}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              subtitle: e.target.value,
                            })
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Image
                        </label>
                        {imagePreview && (
                          <div className="mb-4">
                            <img
                              src={imagePreview}
                              alt="Preview"
                              className="w-32 h-32 object-cover rounded-md"
                            />
                          </div>
                        )}
                        <button
                          type="button"
                          onClick={() => setShowImageSelectDialog(true)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        >
                          {imagePreview ? "Change Image" : "Upload Image"}
                        </button>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Color
                        </label>
                        <input
                          type="color"
                          value={formData.color}
                          onChange={(e) =>
                            setFormData({ ...formData, color: e.target.value })
                          }
                          className="w-full h-10 px-3 py-2 border border-gray-300 rounded-md"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Label
                        </label>
                        <input
                          type="text"
                          value={formData.label}
                          onChange={(e) =>
                            setFormData({ ...formData, label: e.target.value })
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md"
                          placeholder="e.g. NEW, FEATURED etc."
                        />
                      </div>

                      <div>
                        {formData.view === "text_slider" ? (
                          <>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Text Items <span className="text-red-500">*</span>
                            </label>
                            <TextItemsManager
                              textItems={formData.items}
                              onTextItemsChange={(items) =>
                                setFormData({ ...formData, items: items })
                              }
                            />
                          </>
                        ) : (
                          <>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              {formData.view === "combo" ? "Select Combo" : "Items"} <span className="text-red-500">*</span>
                            </label>
                            <ItemSelector
                              type={formData.type}
                              selectedItems={formData.items}
                              onItemsChange={(items) =>
                                setFormData({ ...formData, items: items })
                              }
                              view={formData.view}
                              categories={categories}
                              subCategories={subCategories}
                              services={services}
                              combos={combos}
                            />
                          </>
                        )}
                      </div>
                    </form>
                  </div>

                  <div className="sticky bottom-0 bg-white px-4 sm:px-8 py-4 border-t border-gray-200 rounded-b-lg">
                    <div className="flex justify-end space-x-4">
                      <button
                        type="button"
                        onClick={() => {
                          resetForm();
                          setShowModal(false);
                        }}
                        className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        form="sectionForm"
                        className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                      >
                        {isEditing ? "Update" : "Create"}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      <ImageSelectDialog
        open={showImageSelectDialog}
        onClose={() => setShowImageSelectDialog(false)}
        onFileProcessed={(file) => {
          handleImageSelect(file);
          setShowImageSelectDialog(false);
        }}
      />
    </div>
  );
};

export default AutoHome;